import {Department} from '../components/shared/HierarchyTree'
import {BaseApi} from './BaseApi'
import {AddDepartmentToSiteRequest, UpdateDepartmentRequest} from './siteApi'
import {reportCatchError} from '../utils/errorReporter'

// Re-export from siteApi for consistency
export type {AddDepartmentToSiteRequest, Department, UpdateDepartmentRequest} from './siteApi'

/**
 * Department API service for MSP NodeJS integration
 * Uses the site endpoints for department management
 *
 * @deprecated Use siteApi directly for department operations
 * This file is kept for backwards compatibility
 */
export class DepartmentApi extends BaseApi {
  /**
   * Get all departments for a specific site
   * This method gets the site and extracts departments
   * Based on: GET /api/site/getSite/:id
   */
  async getDepartmentsBySiteId(siteId: string): Promise<Department[]> {
    try {
      const response = await this.client.get<{departments?: Department[]}>(
        `api/site/getSite/${siteId}`,
      )
      return response.data.departments || []
    } catch (error: unknown) {
      reportCatchError('Error fetching departments for site:', error)
      throw error
    }
  }

  /**
   * Create a new department for a site
   * PUT /api/site/addDepartment
   */
  async createDepartmentForSite(
    siteId: string,
    department: AddDepartmentToSiteRequest,
  ): Promise<Department> {
    try {
      const departmentRequest = {
        siteId,
        ...department,
      }
      const response = await this.put<{departments?: Department[]}>(
        'api/site/addDepartment',
        departmentRequest,
      )
      // Return the newly created department (assume it's the last one)
      const departments = response.data.departments || []
      return departments[departments.length - 1]
    } catch (error: unknown) {
      reportCatchError('Error creating department for site:', error)
      throw error
    }
  }

  /**
   * Update an existing department
   * PUT /api/site/updateDepartment
   */
  async updateSiteDepartment(
    siteId: string,
    departmentId: string,
    department: UpdateDepartmentRequest,
  ): Promise<Department> {
    try {
      const response = await this.put<{departments?: Department[]}>(
        `api/site/updateDepartment?siteId=${siteId}&departmentId=${departmentId}`,
        department,
      )
      // Find and return the updated department
      const departments = response.data.departments || []
      const updatedDepartment = departments.find((d) => d.id === departmentId)
      if (!updatedDepartment) {
        throw new Error('Updated department not found in response')
      }
      return updatedDepartment
    } catch (error: unknown) {
      reportCatchError('Error updating department:', error)
      throw error
    }
  }

  /**
   * Delete department from site
   * DELETE /api/site/deleteDepartment
   */
  async deleteDepartment(siteId: string, departmentId: string): Promise<void> {
    try {
      await this.delete(`api/site/deleteDepartment?siteId=${siteId}&departmentId=${departmentId}`)
    } catch (error: unknown) {
      reportCatchError('Error deleting department:', error)
      throw error
    }
  }
}

export const departmentApi = new DepartmentApi()
